import { type MatterSettings } from "@/lib/data/requests";
import {
  createMatterDefaultExpenseV1,
  deleteMatterDefaultExpenseV1,
  updateMatterDefaultExpenseV1,
} from "@/lib/data/requests/matters/matter-default-expenses";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { sortBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateMatterDefaultExpense = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMatterDefaultExpenseV1,
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error creating matter default expense");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter default expense created successfully");
      }
    },
  });
};

export const useUpdateMatterDefaultExpense = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMatterDefaultExpenseV1,
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              defaultExpenses: sortBy(
                oldData.data.defaultExpenses.map((expense) =>
                  expense.id === data.id ? { ...expense, ...data } : expense,
                ),
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating matter default expense");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter default expense updated successfully");
      }
    },
  });
};

export const useDeleteMatterDefaultExpense = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMatterDefaultExpenseV1,
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              defaultExpenses: oldData.data.defaultExpenses.filter(
                (expense) => expense.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter default expense");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter default expense deleted successfully");
      }
    },
  });
};
